import { Component, effect, ElementRef, inject, Injector, OnInit, Renderer2, Signal, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { SheetState } from '@models/types/components/BottomSheet.type';
import { BottomSheetService } from '@services/components/bottom-sheet.service';
import anime, { AnimeInstance } from 'animejs';

@Component({
  selector: 'app-bottom-sheet',
  standalone: true,
  templateUrl: './bottom-sheet.component.html',
  styleUrl: './bottom-sheet.component.css'
})
export class BottomSheetComponent implements OnInit {

  // VIEWCHILDS
  @ViewChild('bottomSheet', {static: true})
  public readonly bottomSheet!:   ElementRef<HTMLDivElement>;

  @ViewChild('sheetContent', { read: ViewContainerRef, static: true })
  public readonly sheetContent!:  ViewContainerRef;

  // SERVICES
  private readonly service:     BottomSheetService      = inject(BottomSheetService);
  private readonly renderer:    Renderer2               = inject(Renderer2);
  private readonly injector:    Injector                = inject(Injector);

  // STATE
  protected readonly state:     Signal<SheetState>          = this.service.state;
  protected readonly component: Signal<Type<unknown>|null>  = this.service.component;
  protected          showSheet: boolean                     = false;

  // DRAG STATE
  public isDragging:    boolean = false;
  private startY:       number  = 0;
  private lastY:        number  = 0;
  private currentY:     number  = 0;
  private velocity:     number  = 0;
  private lastTime:     number  = 0;

  // ANIMATION STATE
  private animation: AnimeInstance | null = null;
  private readonly duration: number       = this.service.duration;

  // THRESHOLDS
  private readonly partialThreshold:  number = 0.45;
  private readonly fullThreshold:     number = 0.95;
  private readonly velocityThreshold: number = 500;

  // LIFECYCLE HOOKS
  public ngOnInit(): void {
    this.initComponent();
  }

  // METHODS
  public onDragStart(event: MouseEvent | TouchEvent): void {
    
    if(this.animation) {
      this.animation.pause();
      this.animation = null;
    }

    this.isDragging = true;
    this.startY     = (event instanceof TouchEvent) ? event.touches[0].clientY : event.clientY;
    this.lastY      = this.startY;
    this.lastTime   = performance.now();
    this.velocity   = 0;

    
    const computedStyle: CSSStyleDeclaration = window.getComputedStyle(this.bottomSheet.nativeElement),
          matrix:        DOMMatrix           = new DOMMatrix(computedStyle.transform);

    this.currentY = matrix.m42;
  }

  public onDragMove(event: MouseEvent | TouchEvent): void {

    if(!this.isDragging) return;

    // USAMOS EL Y DEL EVENTO, NO EL DEL COMPUTED!! (NO USAR METODO GETCURRENTY)
    const currentY:  number = (event instanceof TouchEvent) ? event.touches[0].clientY : event.clientY,
          timestamp: number = performance.now(),
          elapsed:   number = timestamp - this.lastTime;

    if(elapsed > 16) {
      const distance: number = currentY - this.lastY;
      this.velocity = (distance / elapsed) * 1000;
      this.lastY    = currentY;
      this.lastTime = timestamp;
    }

    const deltaY:        number = currentY - this.startY,
          newTranslateY: number = this.currentY + deltaY;
    
    // Limitar el movimiento (no permitir ir más arriba que full, ni más abajo que closed)
    const clampedTranslateY: number = this.getLimitedY(newTranslateY);

    this.renderer.setStyle(this.bottomSheet.nativeElement, 'transform', `translateY(${clampedTranslateY}px)`);

  }

  public onDragEnd(): void {

    if(!this.isDragging) return;
    
    this.isDragging = false;

    const currentY:      number              = this.getCurrentY(),
          newState:      SheetState          = this.determineState(currentY, this.velocity);

    this.animateState(newState);

    this.service.updateFromComponent(newState);
  }
  
  public close(): void {

    this.animateState('closed');

    setTimeout((): void => this.service.close(), this.duration);
    
  }
  
  private determineState(currentTranslateY: number, velocity: number): SheetState {

    const state: SheetState = this.state();

    // Swipe fuerte hacia abajo
    if(velocity > this.velocityThreshold) {
      if(this.isShortViewport) return 'closed';
      return state === 'full' ? 'open' : 'closed';
    }

    // Swipe fuerte hacia arriba
    if(velocity < -this.velocityThreshold) {
      if(this.isShortViewport) return 'full';
      return state === 'closed' ? 'open' : 'full';
    }

    // Sin swipe fuerte, determinar por posición
    const closedY: number = this.getStateHeight('closed'),
          openY:   number = this.getStateHeight('open'),
          fullY:   number = this.getStateHeight('full');

    // Calculamos distancias
    const distToClosed: number = Math.abs(currentTranslateY - closedY),
          distToOpen:   number = Math.abs(currentTranslateY - openY),
          distToFull:   number = Math.abs(currentTranslateY - fullY),
          minDistance:  number = Math.min(distToClosed, distToOpen, distToFull);

    if(minDistance === distToFull) return 'full';
    if(minDistance === distToOpen) return 'open';

    return 'closed';

  }

  protected get isShortViewport(): boolean {
    return window.innerHeight < 500;
  }

  private getStateHeight(state: SheetState): number {

    const screenHeight: number = window.innerHeight;

    switch(state) {
      case 'closed': return screenHeight;
      case 'open':   return this.isShortViewport ? 0 : screenHeight * (1 - this.partialThreshold);
      case 'full':   return this.isShortViewport ? 0 : screenHeight * (1 - this.fullThreshold);
    }

  }
  
  private loadComponent(component: Type<unknown> | null): void {
    
    this.sheetContent.clear();
    if(component) this.sheetContent.createComponent(component, {injector: this.injector});

  }
  
  private setInitialPosition(): void {
    // Establecer posición inicial según el estado actual
    const initialY = this.getStateHeight(this.state());
    this.renderer.setStyle(this.bottomSheet.nativeElement, 'transform', `translateY(${initialY}px)`);
  }
  
  private getCurrentY(): number {

    if(!this.bottomSheet.nativeElement) return 0;

    const computedStyle: CSSStyleDeclaration = window.getComputedStyle(this.bottomSheet.nativeElement),
          matrix:        DOMMatrix           = new DOMMatrix(computedStyle.transform);

    return matrix.m42 ?? 0;

  }

  private getLimitedY(currentY: number): number {

    const screenHeight: number = window.innerHeight,
          minTranslate: number = this.isShortViewport ? 0 : screenHeight * (1 - this.fullThreshold);

    return Math.max(minTranslate, Math.min(screenHeight, currentY));

  }

  private animateState(state: SheetState): void {
    
    if(this.isDragging) return;

    const targetY:   number    = this.getStateHeight(state),
          currentY:  number    = this.getCurrentY(),
          velocity:  number    = Math.abs(this.velocity) * 0.005;

    const proxy:     {translateY: number} = {translateY: currentY};

    if(Math.abs(currentY - targetY) < 1) return;
    
    if(this.animation) {
      this.animation.pause();
    }
    
    this.animation = anime({
      targets:    proxy,
      translateY: targetY,
      duration:   this.duration,
      easing:     `spring(1, 400, 35, ${velocity})`,
      update:     (): void => {
        this.renderer.setStyle(this.bottomSheet.nativeElement, 'transform', `translateY(${proxy.translateY}px)`);
      },
      complete:   (): void => {
        this.animation = null;
        this.velocity = 0;
      }
    });
  }

  private initComponent(): void {
    this.setEffects();
    this.setListeners();
    this.setInitialPosition();
  }
  
  private setEffects(): void {

    // Si el state cambia
    effect((): void => {
      
      const state: SheetState = this.state();
      
      // this.animateState(state);
      
      if(state !== 'closed') {
        this.showSheet = true;
        this.renderer.setAttribute(document.body, 'class', 'overflow-hidden fixed w-full');
        this.animateState(state);     
      }
      else{

        this.renderer.removeAttribute(document.body, 'class');
        this.animateState(state);
        setTimeout((): boolean => this.showSheet = false, this.duration);

      }
      
      
    }, {injector: this.injector});

    // Si el componente cambia
    effect((): void => {

      const component: Type<unknown> | null = this.component();
      if(component) this.loadComponent(component);

    }, {injector: this.injector});
  }
  
  private setListeners(): void {
    this.renderer.listen('window', 'mousemove', this.onDragMove.bind(this));
    this.renderer.listen('window', 'touchmove', this.onDragMove.bind(this));
    this.renderer.listen('window', 'mouseup',   this.onDragEnd.bind(this));
    this.renderer.listen('window', 'touchend',  this.onDragEnd.bind(this));
  }
}